import React, { useEffect, useState } from 'react';
import { apiFetch } from '@/lib/supabaseClient';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';

/**
 * AssignmentScopeSelector — cascading hierarchical selector for
 * Azienda → Locale → Reparto → Mansione.
 *
 * - Fully data-driven: uses /api/companies, /api/locations?company_id=,
 *   /api/departments?location_id=, /api/mansioni?company_id=
 * - Scope-filtered server-side (RBAC).
 * - Never renders UUIDs to the user. UUID stays only as internal <option value>.
 * - Resetting a parent field clears all children.
 *
 * Props:
 *   value: { company_id, location_id, department_id, mansione } (controlled)
 *   onChange: (patch) => void   // merged into parent state
 *   lockLevels: array of 'company'|'location'|'department'|'mansione' (read-only levels, e.g. modify mode)
 *   showDataDate: bool (unused here; parent renders date field)
 *   testidPrefix: string (default 'a')
 */
export default function AssignmentScopeSelector({
  value,
  onChange,
  lockLevels = [],
  testidPrefix = 'a',
  optionalLocation = false,
  optionalMansione = false,
}) {
  const [companies, setCompanies] = useState(null);
  const [locations, setLocations] = useState(null);
  const [departments, setDepartments] = useState(null);
  const [mansioni, setMansioni] = useState(null);

  const isLocked = (k) => lockLevels.includes(k);

  // Load companies (scope-filtered)
  useEffect(() => {
    let m = true;
    apiFetch('/api/companies')
      .then((cs) => m && setCompanies(cs))
      .catch(() => m && setCompanies([]));
    return () => { m = false; };
  }, []);

  // Load locations when Azienda changes
  useEffect(() => {
    if (!value.company_id) { setLocations(null); return; }
    setLocations(null);
    let m = true;
    apiFetch(`/api/locations?company_id=${encodeURIComponent(value.company_id)}`)
      .then((ls) => m && setLocations(ls))
      .catch(() => m && setLocations([]));
    return () => { m = false; };
  }, [value.company_id]);

  // Load departments when Locale changes
  useEffect(() => {
    if (!value.location_id) { setDepartments(null); return; }
    setDepartments(null);
    let m = true;
    apiFetch(`/api/departments?location_id=${encodeURIComponent(value.location_id)}`)
      .then((ds) => m && setDepartments(ds))
      .catch(() => m && setDepartments([]));
    return () => { m = false; };
  }, [value.location_id]);

  // Load mansioni when Azienda changes
  useEffect(() => {
    if (!value.company_id) { setMansioni(null); return; }
    setMansioni(null);
    let m = true;
    apiFetch(`/api/mansioni?company_id=${encodeURIComponent(value.company_id)}`)
      .then((ms) => m && setMansioni(ms))
      .catch(() => m && setMansioni([]));
    return () => { m = false; };
  }, [value.company_id]);

  const chooseCompany = (id) => {
    onChange({ company_id: id || '', location_id: '', department_id: '', mansione: '' });
  };
  const chooseLocation = (id) => {
    onChange({ location_id: id || '', department_id: '' });
  };
  const chooseDepartment = (id) => onChange({ department_id: id || '' });
  const chooseMansione = (name) => onChange({ mansione: name || '' });

  return (
    <div className="space-y-3">
      {/* AZIENDA */}
      <div>
        <Label htmlFor={`${testidPrefix}-company`}>Azienda *</Label>
        <CascadingSelect
          id={`${testidPrefix}-company`}
          testid={`${testidPrefix}-company`}
          value={value.company_id || ''}
          disabled={isLocked('company')}
          onChange={chooseCompany}
          items={companies}
          placeholder="Seleziona azienda"
          emptyMessage="Nessuna azienda accessibile"
          loadingMessage="Caricamento aziende…"
          renderLabel={(c) => c.name}
        />
      </div>

      {/* LOCALE */}
      <div>
        <Label htmlFor={`${testidPrefix}-location`}>{optionalLocation ? 'Locale (facoltativo)' : 'Locale *'}</Label>
        <CascadingSelect
          id={`${testidPrefix}-location`}
          testid={`${testidPrefix}-location`}
          value={value.location_id || ''}
          disabled={isLocked('location') || !value.company_id}
          onChange={chooseLocation}
          items={value.company_id ? locations : []}
          placeholder={value.company_id ? 'Seleziona locale' : 'Prima scegli un\'azienda'}
          emptyMessage="Nessun locale configurato per questa azienda"
          loadingMessage="Caricamento locali…"
          renderLabel={(l) => l.code ? `${l.name} (${l.code})` : l.name}
          allowEmpty={optionalLocation}
          emptyOptionLabel="— Nessun locale (Bozza) —"
        />
      </div>

      {/* REPARTO */}
      <div>
        <Label htmlFor={`${testidPrefix}-department`}>Reparto</Label>
        <CascadingSelect
          id={`${testidPrefix}-department`}
          testid={`${testidPrefix}-department`}
          value={value.department_id || ''}
          disabled={isLocked('department') || !value.location_id}
          onChange={chooseDepartment}
          items={value.location_id ? departments : []}
          placeholder={value.location_id ? 'Seleziona reparto' : 'Prima scegli un locale'}
          emptyMessage="Nessun reparto configurato per questo locale"
          loadingMessage="Caricamento reparti…"
          renderLabel={(d) => d.name}
          allowEmpty
          emptyOptionLabel="— Nessun reparto —"
        />
      </div>

      {/* MANSIONE (uses catalog by name; value = name to satisfy backend job_title) */}
      <div>
        <Label htmlFor={`${testidPrefix}-mansione`}>{optionalMansione ? 'Mansione (facoltativa)' : 'Mansione *'}</Label>
        <CascadingSelect
          id={`${testidPrefix}-mansione`}
          testid={`${testidPrefix}-mansione`}
          value={value.mansione || ''}
          disabled={isLocked('mansione') || !value.company_id}
          onChange={chooseMansione}
          items={value.company_id ? mansioni : []}
          placeholder={value.company_id ? 'Seleziona mansione' : 'Prima scegli un\'azienda'}
          emptyMessage="Nessuna mansione configurata"
          loadingMessage="Caricamento mansioni…"
          renderLabel={(m) => m.name}
          keyField="name"
          allowEmpty={optionalMansione}
          emptyOptionLabel="— Nessuna mansione —"
        />
      </div>
    </div>
  );
}

function CascadingSelect({ id, testid, value, disabled, onChange, items, placeholder, emptyMessage, loadingMessage, renderLabel, allowEmpty = false, emptyOptionLabel = '—', keyField = 'id' }) {
  const loading = items === null;
  const empty = !loading && items.length === 0;
  return (
    <>
      <select
        id={id}
        value={value}
        disabled={disabled || loading || empty}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="h-10 w-full rounded-xl border border-app-border-strong bg-app-surface px-3 text-sm text-app-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-200 disabled:bg-stone-50 disabled:text-stone-400"
      >
        {loading && <option value="">{loadingMessage}</option>}
        {!loading && (
          <>
            <option value="">{allowEmpty ? emptyOptionLabel : placeholder}</option>
            {(items || []).map((it) => (
              <option key={it[keyField]} value={it[keyField]}>{renderLabel(it)}</option>
            ))}
          </>
        )}
      </select>
      {loading && (
        <div className="mt-1 text-xs text-stone-400 flex items-center gap-1" data-testid={`${testid}-loading`}>
          <Loader2 className="w-3 h-3 animate-spin" /> {loadingMessage}
        </div>
      )}
      {empty && (
        <div className="mt-1 text-xs text-amber-700" data-testid={`${testid}-empty`}>{emptyMessage}</div>
      )}
    </>
  );
}
